import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { isSubscriptionExpired } from '@/lib/plans';
import DashboardNav from './components/DashboardNav';
import SubscriptionPanel from './components/SubscriptionPanel';
import StudentDashboard from './components/StudentDashboard';
import MemberDashboard from './components/MemberDashboard';
import OwnerDashboard from './components/OwnerDashboard';
import OperatorDashboard from './components/OperatorDashboard';

export default function Dashboard() {
  const { session, profile, loading, profileError, signOut, refreshProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !session) {
      navigate('/login');
    }
  }, [loading, session, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          <span className="text-sm text-foreground-500">프로필 정보를 불러오는 중...</span>
        </div>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-background-200 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-red-100 text-red-600 mb-4">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">
            프로필 정보를 불러올 수 없습니다
          </h1>
          <p className="text-foreground-500 text-sm leading-relaxed mb-2">
            대시보드로 이동할 수 없습니다. 잠시 후 다시 시도해 주세요.
          </p>
          {profileError && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-6 break-words">
              {profileError}
            </p>
          )}
          <div className="flex flex-col gap-2">
            <button
              onClick={() => refreshProfile()}
              className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-2.5 rounded-md transition-colors cursor-pointer text-sm"
            >
              다시 시도
            </button>
            <button
              onClick={() => signOut()}
              className="whitespace-nowrap border border-background-300 text-foreground-600 hover:bg-background-100 font-semibold px-6 py-2.5 rounded-md transition-colors cursor-pointer text-sm"
            >
              로그아웃
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isPaid = profile.role === 'member' || profile.role === 'owner';
  const isStudent = profile.role === 'student';
  const isExpired =
    (isPaid || isStudent) &&
    (profile.status === 'expired' || isSubscriptionExpired(profile.subscription_expires_at));

  // 학생: 학원 이용기간 만료 시 안내
  if (isStudent && isExpired) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-background-200 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-red-100 text-red-600 mb-4">
            <i className="ri-alert-line text-2xl"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">
            이용기간이 만료되었습니다
          </h1>
          <p className="text-foreground-500 text-sm leading-relaxed mb-6">
            소속 학원의 이용기간이 만료되었습니다. 학원장에게 문의해주세요.
          </p>
          <button
            onClick={() => signOut()}
            className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-2.5 rounded-md transition-colors cursor-pointer text-sm"
          >
            로그아웃
          </button>
        </div>
      </div>
    );
  }

  // 일반회원/학원장: 입금 확인 대기·거절·만료 시 구독 패널만 노출
  if (isPaid && (profile.status === 'pending' || profile.status === 'rejected' || isExpired)) {
    return (
      <div className="min-h-screen bg-background-50">
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-8 space-y-4">
          <SubscriptionPanel profile={profile} onChanged={refreshProfile} />
          <div className="text-center">
            <button
              onClick={() => signOut()}
              className="whitespace-nowrap border border-background-300 text-foreground-600 hover:bg-background-100 font-semibold px-6 py-2.5 rounded-md transition-colors cursor-pointer text-sm"
            >
              로그아웃
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (profile.status === 'pending' || profile.status === 'rejected') {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-background-200 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-4">
            <i className="ri-time-line text-2xl"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">
            {profile.status === 'pending' ? '승인 대기 중' : '가입이 거절되었습니다'}
          </h1>
          <p className="text-foreground-500 text-sm leading-relaxed mb-6">
            {profile.status === 'pending'
              ? '운영자가 가입을 승인하는 중입니다. 승인은 영업일 기준 24시간 이내에 처리됩니다.'
              : '운영자에게 문의해주세요.'}
          </p>
          <button
            onClick={() => signOut()}
            className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-2.5 rounded-md transition-colors cursor-pointer text-sm"
          >
            로그아웃
          </button>
        </div>
      </div>
    );
  }

  const role = profile.role;

  return (
    <div className="min-h-screen bg-background-50">
      <DashboardNav />
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {role === 'operator' ? (
          <OperatorDashboard />
        ) : role === 'owner' ? (
          <OwnerDashboard />
        ) : role === 'member' ? (
          <MemberDashboard />
        ) : (
          <StudentDashboard />
        )}
      </main>
    </div>
  );
}